/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { useState, type ComponentPropsWithoutRef } from 'react';
import { styled } from '@compiled/react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import Heading from '@atlaskit/heading';
import Icon from '@atlaskit/icon';
import { MenuGroup, Section } from '@atlaskit/menu';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { AutomationGlyph } from '@atlassian/jira-automation-menu/src/common/assets/automation.tsx';
import MenuButton from '@atlassian/jira-automation-menu/src/ui/button/main.tsx';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import type { ProjectProps } from '../common/types.tsx';
import { AutomationMenuDeepLinks } from './menu-contents/deep-links/AutomationMenuDeepLinks.tsx';
import { AutomationMenuTemplateRules } from './menu-contents/template-rules/AutomationMenuTemplateRules.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line @atlaskit/design-system/no-custom-icons
const automationIcon = <Icon glyph={AutomationGlyph} label="" />;

const AutomationMenuContent = (props: ProjectProps) => {
	const { projectType } = props;
	const intl = useIntl();
	return projectType === SOFTWARE_PROJECT ? (
		<MenuContentWrapper data-testid="jira-releases-automation-menu.ui.automation-menu-content">
			<MenuTitle>{intl.formatMessage(messages.automationMenuTitle)}</MenuTitle>
			<MenuSubTitle>{intl.formatMessage(messages.automationMenuSubTitle)}</MenuSubTitle>
			<MenuGroup>
				<AutomationMenuTemplateRules {...props} />
				<Section hasSeparator>
					<AutomationMenuDeepLinks includeDiscoverAutomation={false} {...props} />
				</Section>
			</MenuGroup>
		</MenuContentWrapper>
	) : (
		<AutomationMenuDeepLinks includeDiscoverAutomation {...props} />
	);
};

export type Props = {
	isMenuEnabled: boolean;
	projectProps?: ProjectProps;
	// for VR testing
	isOpenByDefault?: boolean;
};

export const AutomationMenu = ({
	isMenuEnabled,
	projectProps,
	isOpenByDefault,
}: Props): React.JSX.Element => {
	const disabledAutomationMenuButtonTooltipMessageGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.disabledAutomationMenuButtonTooltipMessageGalaxia
		: messages.disabledAutomationMenuButtonTooltipMessage;
	const [isOpen, setIsOpen] = useState<boolean>(isOpenByDefault ?? false);

	const intl = useIntl();

	return isMenuEnabled && projectProps ? (
		<SpotlightManager blanketIsTinted={false}>
			<SpotlightTarget name="fusionReleaseAutomationMenu">
				<Popup
					isOpen={isOpen}
					onClose={() => setIsOpen(false)}
					placement="bottom-end"
					messageId="software-releases-automation-menu.ui.popup"
					messageType="transactional"
					content={() => (
						<AutomationMenuContent
							projectKey={projectProps.projectKey}
							projectType={projectProps.projectType}
							isSimplifiedProject={projectProps.isSimplifiedProject}
						/>
					)}
					trigger={({ ...triggerProps }) => (
						<div data-testid="jira-releases-automation-menu.ui.automation-button.enabled">
							<MenuButton
								{...triggerProps}
								onClick={() => setIsOpen(!isOpen)}
								isSelected={isOpen}
							/>
						</div>
					)}
				/>
			</SpotlightTarget>
			<EngagementSpotlight name="fusionReleaseAutomationMenu" />
		</SpotlightManager>
	) : (
		<IconWrapper data-testid="jira-releases-automation-menu.ui.automation-button.disabled">
			<Tooltip
				position="top"
				content={intl.formatMessage(disabledAutomationMenuButtonTooltipMessageGalaxia)}
			>
				{automationIcon}
			</Tooltip>
		</IconWrapper>
	);
};

export default AutomationMenu;

const MenuTitle = (props: ComponentPropsWithoutRef<typeof MenuTitleComponent>) => (
	<Heading as={fg('jfp_a11y_autodev_fix_releases_automation_heading') ? 'h2' : 'h4'} size="small">
		<MenuTitleComponent {...props} />
	</Heading>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuContentWrapper = styled.div({
	width: '400px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuSubTitle = styled.div({
	color: token('color.text.subtlest'),
	paddingTop: '0px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingRight: '56px',
	paddingBottom: token('space.200'),
	paddingLeft: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const IconWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	height: '28px',
	width: '28px',
	justifyContent: 'center',

	color: `${token('color.icon.disabled')}`,
	paddingTop: `${token('space.050')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		cursor: 'not-allowed',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuTitleComponent = styled.div({
	paddingTop: token('space.300'),
	paddingRight: '0px',
	paddingBottom: token('space.100'),
	paddingLeft: token('space.300'),
});
